import {
  Activity as PreservedView,
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Activity } from "./Activity";
import { Brand } from "./Brand";
import { t, type Locale } from "./i18n";
import { Locations } from "./Locations";
import { OperatorOnboarding } from "./OperatorOnboarding";
import { Overview } from "./Overview";
import { ProgramBuilder } from "./ProgramBuilder";
import { ScannerCheckout, type TillDeviceSession } from "./ScannerCheckout";
import { ShopProfile } from "./ShopProfile";
import { StaffDevices } from "./StaffDevices";

export type MerchantRole = "owner" | "manager" | "cashier";

export interface MerchantSession {
  user: {
    id: string;
    displayName: string;
    preferredLocale: Locale;
  };
  merchants: Array<{
    merchant: {
      id: string;
      slug: string;
      displayName: string;
      shortDescription: string | null;
    };
    roles: MerchantRole[];
    allowedLocationIds: string[];
  }>;
  expiresAt: string;
}

export type AuthState =
  | { status: "loading" }
  | { status: "signed-out" }
  | { status: "authenticated"; session: MerchantSession }
  | { status: "error" };

export type Page =
  | "overview"
  | "scanner"
  | "program"
  | "shop"
  | "locations"
  | "staff"
  | "transactions";

type Destination = Page | "sign-in";
type Copy = (key: string, values?: Record<string, string | number>) => string;
type Surface = "manage" | "till" | "operator";
type AuthMethod = "otp" | "access-code";

const managementPages: Array<Exclude<Page, "scanner">> = [
  "overview",
  "transactions",
  "program",
  "shop",
  "locations",
  "staff",
];
const API_ORIGIN = (import.meta.env.VITE_API_ORIGIN ?? "").replace(/\/$/, "");

interface NavigationEntry {
  surface: Surface;
  page: Page;
  hasManagePredecessor: boolean;
}

function readNavigationEntry(): NavigationEntry | null {
  if (typeof window === "undefined") return null;
  const entry = window.history.state?.collectieNavigation;
  if (
    !entry ||
    entry.surface !== surfaceFromPath() ||
    ![...managementPages, "scanner"].includes(entry.page) ||
    typeof entry.hasManagePredecessor !== "boolean"
  )
    return null;
  return entry;
}

function writeNavigationEntry(
  method: "push" | "replace",
  surface: Surface,
  page: Page,
  url = window.location.href,
  hasManagePredecessor = false,
) {
  window.history[method === "push" ? "pushState" : "replaceState"](
    { collectieNavigation: { surface, page, hasManagePredecessor } },
    "",
    url,
  );
}

function surfaceFromPath(): Surface {
  if (typeof window === "undefined") return "manage";
  if (window.location.pathname === "/operator") return "operator";
  if (
    window.location.pathname === "/till" ||
    window.location.pathname === "/scan"
  ) {
    return "till";
  }
  return "manage";
}

function hasManagementRole(session: MerchantSession): boolean {
  return session.merchants.some(({ roles }) =>
    roles.some((role) => role === "owner" || role === "manager"),
  );
}

export function routeForSession(
  session: MerchantSession | null,
  requestedPage: Page,
): Destination {
  if (!session || session.merchants.length === 0) return "sign-in";
  if (!hasManagementRole(session)) return "scanner";
  return requestedPage;
}

async function readProblem(response: Response): Promise<string> {
  const body = await response.json().catch(() => null);
  if (
    body &&
    typeof body === "object" &&
    "error" in body &&
    body.error &&
    typeof body.error === "object" &&
    "code" in body.error &&
    typeof body.error.code === "string"
  ) {
    return body.error.code;
  }
  return "UNEXPECTED_RESPONSE";
}

async function postJson(
  path: string,
  body: Record<string, string>,
): Promise<Response> {
  return fetch(`${API_ORIGIN}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function App({
  initialAuth,
  initialAccessCodeEnabled,
  initialAuthMethod = "otp",
  initialLocale,
  initialPage,
  initialSurface,
}: {
  initialAuth?: AuthState;
  initialAccessCodeEnabled?: boolean;
  initialAuthMethod?: AuthMethod;
  initialLocale?: Locale;
  initialPage?: Page;
  initialSurface?: Surface;
}) {
  const [surface, setSurface] = useState<Surface>(
    initialSurface ?? surfaceFromPath(),
  );
  const accessCodeEnabled =
    initialAccessCodeEnabled ??
    (typeof window !== "undefined" &&
      window.location.hostname.includes("-staging."));
  const [auth, setAuth] = useState<AuthState>(
    initialAuth ?? { status: "loading" },
  );
  const [locale, setLocale] = useState<Locale>(initialLocale ?? "en");
  const [requestedPage, setRequestedPage] = useState<Page>(
    () => initialPage ?? readNavigationEntry()?.page ?? "overview",
  );
  const [signingOut, setSigningOut] = useState(false);
  const [signOutFailed, setSignOutFailed] = useState(false);
  const copy = useMemo<Copy>(
    () =>
      (key, values = {}) =>
        t(locale, key, values),
    [locale],
  );

  useEffect(() => {
    if (initialLocale || typeof window === "undefined") return;
    const stored = window.localStorage.getItem(
      surface === "till" ? "collectie_till_locale" : "collectie_manage_locale",
    );
    if (stored === "en" || stored === "zh-Hant" || stored === "de") {
      setLocale(stored);
    }
  }, [initialLocale, surface]);

  const selectLocale = useCallback(
    (nextLocale: Locale) => {
      setLocale(nextLocale);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(
          surface === "till"
            ? "collectie_till_locale"
            : "collectie_manage_locale",
          nextLocale,
        );
        document.documentElement.lang = nextLocale;
      }
    },
    [surface],
  );

  useEffect(() => {
    if (initialAuth || surface !== "manage") return;
    const controller = new AbortController();
    fetch(`${API_ORIGIN}/v1/merchant/session`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (response.ok) {
          const session = (await response.json()) as MerchantSession;
          setAuth({ status: "authenticated", session });
          if (!initialLocale) selectLocale(session.user.preferredLocale);
          return;
        }
        setAuth(
          response.status === 401
            ? { status: "signed-out" }
            : { status: "error" },
        );
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setAuth({ status: "error" });
        }
      });
    return () => controller.abort();
  }, [initialAuth, initialLocale, selectLocale, surface]);

  useEffect(() => {
    // Initialize this document once. Marked Till entries already have a real
    // Manage predecessor, including after reload and Strict Mode effect replay.
    const entry = readNavigationEntry();
    if (surface === "till" && !entry?.hasManagePredecessor) {
      const tillUrl =
        surfaceFromPath() === "till" ? window.location.href : "/till";
      writeNavigationEntry("replace", "manage", "overview", "/manage");
      writeNavigationEntry("push", "till", "overview", tillUrl, true);
    } else if (!entry) {
      writeNavigationEntry("replace", surface, requestedPage);
    }
    const restoreNavigation = () => {
      const nextSurface = surfaceFromPath();
      const nextEntry = readNavigationEntry();
      setSurface(nextSurface);
      setRequestedPage(nextEntry?.page ?? "overview");
    };
    window.addEventListener("popstate", restoreNavigation);
    return () => window.removeEventListener("popstate", restoreNavigation);
  }, []);

  const navigatePage = (page: Page) => {
    writeNavigationEntry("replace", "manage", page);
    setRequestedPage(page);
  };

  const navigateSurface = (next: "manage" | "till") => {
    if (next === "till") {
      // Save only the destination, never form values or pairing credentials.
      writeNavigationEntry("replace", "manage", requestedPage);
      writeNavigationEntry("push", "till", requestedPage, "/till", true);
    } else {
      writeNavigationEntry("replace", "manage", "overview", "/manage");
      setRequestedPage("overview");
    }
    setSurface(next);
  };

  const returnFromTill = () => {
    if (readNavigationEntry()?.hasManagePredecessor) window.history.back();
    else navigateSurface("manage");
  };

  const signOut = async () => {
    setSigningOut(true);
    setSignOutFailed(false);
    try {
      const response = await fetch("/v1/merchant/auth/sign-out", {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) {
        setSignOutFailed(true);
        return;
      }
      setAuth({ status: "signed-out" });
      setRequestedPage("overview");
    } catch {
      setSignOutFailed(true);
    } finally {
      setSigningOut(false);
    }
  };

  if (surface === "operator") {
    return (
      <OperatorOnboarding copy={copy} locale={locale} onLocale={selectLocale} />
    );
  }

  const renderManagement = () => {
    if (auth.status === "loading") return <LoadingScreen copy={copy} />;
    if (auth.status === "error") {
      return (
        <StatusScreen
          copy={copy}
          locale={locale}
          onLocale={selectLocale}
          onRetry={() => {
            setAuth({ status: "loading" });
            window.location.reload();
          }}
        />
      );
    }
    if (auth.status === "signed-out") {
      return (
        <SignIn
          copy={copy}
          accessCodeEnabled={accessCodeEnabled}
          initialAuthMethod={initialAuthMethod}
          locale={locale}
          onLocale={selectLocale}
          onOpenTill={() => navigateSurface("till")}
          onAuthenticated={(session) => {
            setAuth({ status: "authenticated", session });
            selectLocale(session.user.preferredLocale);
            setRequestedPage(
              hasManagementRole(session) ? "overview" : "scanner",
            );
          }}
        />
      );
    }

    const destination = routeForSession(auth.session, requestedPage);
    if (destination === "sign-in") {
      return (
        <SignIn
          copy={copy}
          accessCodeEnabled={accessCodeEnabled}
          initialAuthMethod={initialAuthMethod}
          locale={locale}
          onLocale={selectLocale}
          onOpenTill={() => navigateSurface("till")}
          onAuthenticated={(session) => {
            setAuth({ status: "authenticated", session });
            selectLocale(session.user.preferredLocale);
            setRequestedPage(
              hasManagementRole(session) ? "overview" : "scanner",
            );
          }}
        />
      );
    }
    if (destination === "scanner") {
      const onManage = hasManagementRole(auth.session)
        ? () => {
            setRequestedPage("overview");
            navigateSurface("manage");
          }
        : null;
      return (
        <TillShell
          copy={copy}
          locale={locale}
          onLocale={selectLocale}
          {...(onManage ? { onManage } : {})}
        />
      );
    }

    return (
      <ManagementShell
        copy={copy}
        locale={locale}
        page={destination}
        session={auth.session}
        onLocale={selectLocale}
        onNavigate={navigatePage}
        onOpenTill={() => navigateSurface("till")}
        onSignOut={() => void signOut()}
        signingOut={signingOut}
        signOutFailed={signOutFailed}
      />
    );
  };

  return (
    <>
      <PreservedView mode={surface === "manage" ? "visible" : "hidden"}>
        {renderManagement()}
      </PreservedView>
      {surface === "till" && (
        <TillShell
          copy={copy}
          locale={locale}
          onLocale={selectLocale}
          onBack={returnFromTill}
          onManage={() => navigateSurface("manage")}
        />
      )}
    </>
  );
}

function LocaleOptions({
  copy,
  locale,
  onLocale,
}: {
  copy: Copy;
  locale: Locale;
  onLocale: (locale: Locale) => void;
}) {
  return (
    <div
      className="locale-options"
      role="group"
      aria-label={copy("language.choose")}
    >
      <button
        type="button"
        aria-pressed={locale === "en"}
        onClick={() => onLocale("en")}
      >
        {copy("language.english")}
      </button>
      <button
        type="button"
        aria-pressed={locale === "zh-Hant"}
        onClick={() => onLocale("zh-Hant")}
      >
        {copy("language.traditionalChinese")}
      </button>
      <button
        type="button"
        aria-pressed={locale === "de"}
        onClick={() => onLocale("de")}
      >
        {copy("language.german")}
      </button>
    </div>
  );
}

function LocaleMenu({
  copy,
  locale,
  onLocale,
}: {
  copy: Copy;
  locale: Locale;
  onLocale: (locale: Locale) => void;
}) {
  return (
    <details className="locale-menu">
      <summary>
        {copy("language.current", {
          language: copy(
            locale === "en"
              ? "language.english"
              : locale === "de"
                ? "language.german"
                : "language.traditionalChinese",
          ),
        })}
      </summary>
      <LocaleOptions copy={copy} locale={locale} onLocale={onLocale} />
    </details>
  );
}

function LoadingScreen({ copy }: { copy: Copy }) {
  return (
    <main className="centered-screen" data-state="loading" aria-busy="true">
      <div className="loading-mark" aria-hidden="true" />
      <p>{copy("merchant.shell.loading")}</p>
    </main>
  );
}

function StatusScreen({
  copy,
  locale,
  onLocale,
  onRetry,
}: {
  copy: Copy;
  locale: Locale;
  onLocale: (locale: Locale) => void;
  onRetry: () => void;
}) {
  return (
    <main className="centered-screen status-screen">
      <Brand />
      <section className="status-card" role="alert">
        <p className="eyebrow">{copy("merchant.shell.connectionLabel")}</p>
        <h1>{copy("merchant.shell.unavailableTitle")}</h1>
        <p>{copy("merchant.shell.unavailableBody")}</p>
        <button className="primary-button" type="button" onClick={onRetry}>
          {copy("common.retry")}
        </button>
      </section>
      <LocaleMenu copy={copy} locale={locale} onLocale={onLocale} />
    </main>
  );
}

function SignIn({
  copy,
  accessCodeEnabled,
  initialAuthMethod,
  locale,
  onLocale,
  onOpenTill,
  onAuthenticated,
}: {
  copy: Copy;
  accessCodeEnabled: boolean;
  initialAuthMethod: AuthMethod;
  locale: Locale;
  onLocale: (locale: Locale) => void;
  onOpenTill: () => void;
  onAuthenticated: (session: MerchantSession) => void;
}) {
  const [email, setEmail] = useState("");
  const [requestId, setRequestId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [localCode, setLocalCode] = useState<string | null>(null);
  const [authMethod, setAuthMethod] = useState<AuthMethod>(initialAuthMethod);
  const [accessCode, setAccessCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const requestCode = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setErrorCode(null);
    try {
      const response = await postJson("/v1/merchant/auth/otp/request", {
        email,
      });
      if (!response.ok) {
        setErrorCode(await readProblem(response));
        return;
      }
      const receipt = (await response.json()) as {
        requestId: string;
        localDeliveryCode?: string;
      };
      setRequestId(receipt.requestId);
      setLocalCode(receipt.localDeliveryCode ?? null);
    } catch {
      setErrorCode("NETWORK_ERROR");
    } finally {
      setSubmitting(false);
    }
  };

  const verifyCode = async (event: FormEvent) => {
    event.preventDefault();
    if (!requestId) return;
    setSubmitting(true);
    setErrorCode(null);
    try {
      const response = await postJson("/v1/merchant/auth/otp/verify", {
        requestId,
        code,
      });
      if (!response.ok) {
        setErrorCode(await readProblem(response));
        return;
      }
      onAuthenticated((await response.json()) as MerchantSession);
    } catch {
      setErrorCode("NETWORK_ERROR");
    } finally {
      setSubmitting(false);
    }
  };

  const useAccessCode = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setErrorCode(null);
    try {
      const response = await postJson("/v1/merchant/auth/access-code", {
        accessKey: accessCode,
      });
      if (!response.ok) {
        setErrorCode(await readProblem(response));
        return;
      }
      setAccessCode("");
      onAuthenticated((await response.json()) as MerchantSession);
    } catch {
      setErrorCode("NETWORK_ERROR");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="auth-screen">
      <header className="auth-brand">
        <Brand />
      </header>
      <div className="auth-layout">
        <section className="auth-intro">
          <p className="eyebrow">{copy("merchant.auth.eyebrow")}</p>
          <h1>{copy("merchant.auth.title")}</h1>
          <p>{copy("merchant.auth.body")}</p>
          <div className="trust-note">
            <strong>{copy("merchant.auth.noPasswordTitle")}</strong>
            <span>{copy("merchant.auth.noPasswordBody")}</span>
          </div>
        </section>
        <section
          className="auth-card"
          aria-label={copy("merchant.auth.formLabel")}
        >
          {accessCodeEnabled && authMethod === "access-code" && (
            <form onSubmit={useAccessCode}>
              <button
                className="text-button auth-back"
                type="button"
                onClick={() => {
                  setAuthMethod("otp");
                  setAccessCode("");
                  setErrorCode(null);
                }}
              >
                {copy("merchant.auth.useEmailCode")}
              </button>
              <h2>{copy("merchant.auth.accessCodeTitle")}</h2>
              <p className="field-help">
                {copy("merchant.auth.accessCodeBody")}
              </p>
              <label htmlFor="merchant-access-code">
                {copy("merchant.auth.accessCode")}
              </label>
              <input
                id="merchant-access-code"
                type="password"
                autoComplete="off"
                minLength={32}
                maxLength={256}
                value={accessCode}
                onChange={(event) => setAccessCode(event.target.value)}
                required
              />
              {errorCode && (
                <p className="form-error access-code-error" role="alert">
                  {copy("merchant.auth.accessCodeError")}
                </p>
              )}
              <button
                className="primary-button"
                type="submit"
                disabled={submitting || accessCode.length < 32}
              >
                {copy(
                  submitting
                    ? "merchant.auth.accessCodeSigningIn"
                    : "merchant.auth.accessCodeContinue",
                )}
              </button>
            </form>
          )}
          {(!accessCodeEnabled || authMethod !== "access-code") &&
            !requestId && (
              <form onSubmit={requestCode}>
                <h2>{copy("merchant.auth.emailTitle")}</h2>
                <label htmlFor="merchant-email">
                  {copy("merchant.auth.email")}
                </label>
                <input
                  id="merchant-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
                <p className="field-help">{copy("merchant.auth.emailHelp")}</p>
                {errorCode && (
                  <p className="form-error" role="alert">
                    {copy("merchant.auth.requestError")}
                  </p>
                )}
                <button
                  className="primary-button"
                  type="submit"
                  disabled={submitting}
                >
                  {copy(
                    submitting
                      ? "merchant.auth.sending"
                      : "merchant.auth.sendCode",
                  )}
                </button>
                {accessCodeEnabled && (
                  <button
                    className="text-button auth-method-switch"
                    type="button"
                    onClick={() => {
                      setAuthMethod("access-code");
                      setErrorCode(null);
                    }}
                  >
                    {copy("merchant.auth.useAccessCode")}
                  </button>
                )}
              </form>
            )}
          {(!accessCodeEnabled || authMethod !== "access-code") &&
            requestId && (
              <form onSubmit={verifyCode}>
                <button
                  className="text-button auth-back"
                  type="button"
                  onClick={() => {
                    setRequestId(null);
                    setCode("");
                    setLocalCode(null);
                    setErrorCode(null);
                  }}
                >
                  {copy("merchant.auth.changeEmail")}
                </button>
                <h2>{copy("merchant.auth.codeTitle")}</h2>
                <label htmlFor="merchant-code">
                  {copy("merchant.auth.code")}
                </label>
                <input
                  id="merchant-code"
                  className="code-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  pattern="[0-9]{6}"
                  value={code}
                  onChange={(event) =>
                    setCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  required
                />
                <p className="field-help">
                  {copy("merchant.auth.codeHelp", { email })}
                </p>
                {localCode && (
                  <p className="local-code">
                    {copy("merchant.auth.localCode", { code: localCode })}
                  </p>
                )}
                {errorCode && (
                  <p className="form-error" role="alert">
                    {copy("merchant.auth.verifyError")}
                  </p>
                )}
                <button
                  className="primary-button"
                  type="submit"
                  disabled={submitting || code.length !== 6}
                >
                  {copy(
                    submitting
                      ? "merchant.auth.verifying"
                      : "merchant.auth.verify",
                  )}
                </button>
              </form>
            )}
        </section>
      </div>
      <footer className="auth-footer">
        <LocaleMenu copy={copy} locale={locale} onLocale={onLocale} />
        <button className="text-button" type="button" onClick={onOpenTill}>
          {copy("merchant.auth.openTill")}
        </button>
      </footer>
    </main>
  );
}

function NavigationIcon({ page }: { page: Exclude<Page, "scanner"> }) {
  return <span className={`nav-icon nav-icon-${page}`} aria-hidden="true" />;
}

function ManagementShell({
  copy,
  locale,
  page,
  session,
  onLocale,
  onNavigate,
  onOpenTill,
  onSignOut,
  signingOut,
  signOutFailed,
}: {
  copy: Copy;
  locale: Locale;
  page: Exclude<Page, "scanner">;
  session: MerchantSession;
  onLocale: (locale: Locale) => void;
  onNavigate: (page: Page) => void;
  onOpenTill: () => void;
  onSignOut: () => void;
  signingOut: boolean;
  signOutFailed: boolean;
}) {
  const scope = session.merchants[0]!;
  const [merchantName, setMerchantName] = useState(scope.merchant.displayName);
  const [activeLocationCount, setActiveLocationCount] = useState(
    scope.allowedLocationIds.length,
  );
  const titleKey: Record<Exclude<Page, "scanner">, string> = {
    overview: "merchant.overview.welcome",
    transactions: "merchant.activity.title",
    program: "merchant.program.title",
    shop: "merchant.shop.title",
    locations: "merchant.nav.locations",
    staff: "merchant.staff.title",
  };

  return (
    <div className="management-shell" data-mode="management">
      <aside className="management-sidebar">
        <Brand />
        <nav aria-label={copy("merchant.shell.managementNavigation")}>
          {managementPages.map((item) => (
            <button
              type="button"
              key={item}
              aria-current={page === item ? "page" : undefined}
              onClick={() => onNavigate(item)}
            >
              <NavigationIcon page={item} />
              {copy(`merchant.nav.${item}`)}
            </button>
          ))}
        </nav>
        <button className="open-till-button" type="button" onClick={onOpenTill}>
          <span aria-hidden="true" />
          <span>
            <strong>{copy("merchant.till.open")}</strong>
            <small>{copy("merchant.till.openHint")}</small>
          </span>
        </button>
        <details className="workspace-menu">
          <summary>
            <span className="workspace-avatar" aria-hidden="true">
              {merchantName.slice(0, 1)}
            </span>
            <span>
              <strong>{merchantName}</strong>
              <small>{session.user.displayName}</small>
            </span>
          </summary>
          <div className="workspace-menu-popover">
            <p>
              {copy("merchant.shell.locationCount", {
                count: new Intl.NumberFormat(locale).format(
                  activeLocationCount,
                ),
              })}
            </p>
            <span className="menu-label">{copy("language.choose")}</span>
            <LocaleOptions copy={copy} locale={locale} onLocale={onLocale} />
            <button
              className="sidebar-sign-out"
              type="button"
              disabled={signingOut}
              onClick={onSignOut}
            >
              {copy(
                signingOut
                  ? "merchant.auth.signingOut"
                  : "merchant.auth.signOut",
              )}
            </button>
            {signOutFailed && (
              <span className="sidebar-error" role="alert">
                {copy("merchant.auth.signOutError")}
              </span>
            )}
          </div>
        </details>
      </aside>
      <main className="management-main" id="main-content">
        <header className="management-header">
          <div>
            <p className="page-context">{merchantName}</p>
            <h1>{copy(titleKey[page], { name: session.user.displayName })}</h1>
            <p>{copy(`merchant.pageDescription.${page}`)}</p>
          </div>
          <button
            className="primary-button compact desktop-till-action"
            type="button"
            onClick={onOpenTill}
          >
            {copy("merchant.till.open")}
          </button>
        </header>
        {page === "overview" && (
          <Overview
            merchantId={scope.merchant.id}
            copy={copy}
            locale={locale}
            onScan={onOpenTill}
          />
        )}
        {page === "program" && (
          <ProgramBuilder
            merchantId={scope.merchant.id}
            locale={locale}
            copy={copy}
          />
        )}
        {page === "shop" && (
          <ShopProfile
            merchantId={scope.merchant.id}
            canManage={scope.roles.some(
              (role) => role === "owner" || role === "manager",
            )}
            locale={locale}
            copy={copy}
            onSavedName={setMerchantName}
          />
        )}
        {page === "transactions" && (
          <Activity
            merchantId={scope.merchant.id}
            canReverse={scope.roles.some(
              (role) => role === "owner" || role === "manager",
            )}
            locale={locale}
            copy={copy}
          />
        )}
        {page === "locations" && (
          <Locations
            merchantId={scope.merchant.id}
            canManage={scope.roles.some(
              (role) => role === "owner" || role === "manager",
            )}
            locale={locale}
            copy={copy}
            onActiveCount={setActiveLocationCount}
            onCustomizeQr={() => onNavigate("shop")}
          />
        )}
        {page === "staff" && (
          <StaffDevices
            merchantId={scope.merchant.id}
            canManage={scope.roles.some(
              (role) => role === "owner" || role === "manager",
            )}
            locale={locale}
            copy={copy}
          />
        )}
      </main>
      <nav
        className="mobile-navigation"
        aria-label={copy("merchant.shell.managementNavigation")}
      >
        {managementPages.map((item) => (
          <button
            type="button"
            key={item}
            aria-current={page === item ? "page" : undefined}
            onClick={() => onNavigate(item)}
          >
            <NavigationIcon page={item} />
            {copy(`merchant.nav.${item}`)}
          </button>
        ))}
      </nav>
      <button
        className="mobile-till-action"
        type="button"
        onClick={onOpenTill}
        aria-label={copy("merchant.till.open")}
      >
        <span aria-hidden="true" />
      </button>
    </div>
  );
}

function TillShell({
  copy,
  locale,
  onLocale,
  onManage,
  onBack,
}: {
  copy: Copy;
  locale: Locale;
  onLocale: (locale: Locale) => void;
  onManage?: () => void;
  onBack?: () => void;
}) {
  const [deviceSession, setDeviceSession] = useState<TillDeviceSession | null>(
    null,
  );
  const handleDeviceSession = useCallback(
    (session: TillDeviceSession | null) => {
      setDeviceSession(session);
    },
    [],
  );

  return (
    <main className="scanner-shell" data-mode="till">
      <header className="scanner-header">
        <Brand />
        <div className="scanner-location">
          <strong>
            {deviceSession?.device.label ??
              copy("merchant.till.checkingDevice")}
          </strong>
          <span>
            {deviceSession
              ? deviceSession.location.displayName
              : copy("merchant.till.sharedCounter")}
          </span>
        </div>
        <details className="till-menu">
          <summary aria-label={copy("merchant.till.menu")}>
            <span aria-hidden="true" />
          </summary>
          <div>
            {deviceSession && (
              <p>
                <strong>{deviceSession.device.label}</strong>
                <span>{deviceSession.location.displayName}</span>
              </p>
            )}
            <span className="menu-label">{copy("language.choose")}</span>
            <LocaleOptions copy={copy} locale={locale} onLocale={onLocale} />
            {onManage ? (
              <button type="button" onClick={onManage}>
                {copy("merchant.till.manageBusiness")}
              </button>
            ) : (
              <a href="/manage">{copy("merchant.till.manageBusiness")}</a>
            )}
          </div>
        </details>
        <nav
          className="till-navigation"
          aria-label={copy("merchant.till.navigation")}
        >
          {onBack && (
            <button className="quiet-button" type="button" onClick={onBack}>
              {copy("merchant.till.back")}
            </button>
          )}
          {onManage ? (
            <button className="quiet-button" type="button" onClick={onManage}>
              {copy("merchant.till.mainPage")}
            </button>
          ) : (
            <a className="quiet-button" href="/manage">
              {copy("merchant.till.mainPage")}
            </a>
          )}
        </nav>
      </header>
      <ScannerCheckout
        copy={copy}
        locale={locale}
        onDeviceSession={handleDeviceSession}
      />
    </main>
  );
}
